import IaaiEventBus from "@/events";
import {AUCTION_EVENT_IAAI, IAAI_EVENT_AUCTION_JOINED, IAAI_EVENT_LEAVE_AUCTION_CONFIRMATION} from "@/consts";
import store from "@/store";
import {SET_CONNECTED_CLIENT, SET_ERROR_CONNECTED} from "@/store/mutations.type";
import * as Sentry from "@sentry/vue";
import {sendTrackingEvent} from "@/utils/tracking";
const AuctionNow = require('./assets/js/AuctionNow')

class IaaiService {

    constructor() {
        this.iaaiEventBus = null;
        this.iaaiClient = null;
    }

    /**
     *
     * @param {string} bearerToken
     */
    init(bearerToken) {
        if(bearerToken) {

            const configuration = {
                // prod values
                "portalApiUrl": process.env.VUE_APP_PORTAL_API_URL,
                "systemHubUrl": process.env.VUE_APP_SYSTEM_HUB_URL,
                // test values
                // "portalApiUrl": process.env.VUE_APP_PORTAL_API_URL_TEST,
                // "systemHubUrl": process.env.VUE_APP_SYSTEM_HUB_URL_TEST,
                "bearerToken": bearerToken,
            }

            this.iaaiClient = new AuctionNow.Client(configuration);
            const iaaiEventBusInit = new IaaiEventBus
            this.iaaiEventBus = iaaiEventBusInit.init(this.iaaiClient, this)
        } else {
            store.commit(SET_CONNECTED_CLIENT, false);
            store.commit(SET_ERROR_CONNECTED, 'An error has occurred. Try again.');
            // Log missing token to Sentry for visibility
            Sentry.captureMessage('IaaiService init failed: missing bearer token', {
                level: 'warning',
                tags: {
                    feature: 'init',
                    errorType: 'missing_token',
                },
                extra: {
                    reason: 'No bearerToken provided on init',
                }
            });
        }
    }

    /**
     * join to auction
     *
     * @param {string} InternalAuctionId
     * @param {string} ExternalAuctionId
     * @param {function} successCallback
     * @param {function} failCallback
     */
    joinToAuction(InternalAuctionId, ExternalAuctionId, successCallback, failCallback) {
        /**
         * event subscription
         */
        this.iaaiEventBus.$on(AUCTION_EVENT_IAAI + InternalAuctionId, async function (payload) {
            switch (payload.iaaiEvent) {
                case IAAI_EVENT_AUCTION_JOINED:
                    successCallback(false)
                    break
                case IAAI_EVENT_LEAVE_AUCTION_CONFIRMATION:
                    successCallback(true)
                    break
                default:
                    failCallback(new Error("Unexpected iaai event: " + payload.iaaiEvent))
                    break
            }
        });

        this.iaaiClient.join("iaai", ExternalAuctionId);
    }

    /**
     *
     * @param {string} InternalAuctionId
     * @param {function} successCallback
     */
    leaveAuction(InternalAuctionId, successCallback) {
        this.iaaiClient.leave(InternalAuctionId)
            .then(function () {
                successCallback();
            })
            .catch(function (error) {
                console.log('error', error)
            })
    }

    /**
     * Checks if the bid amount is within spending limits
     * @param {number} amount - Bid amount to check
     * @returns {{isValid: boolean, errorMessage: string, moneyLimit: number, carsLimit: number}} - Validation result
     * @private
     */
    _checkSpendingLimits(amount) {
        const storageLimit = store.state.auctions.spendingLimit;
        const cookieName = 'sold_cars_info';

        // If spending limit is not set (null/undefined), allow all bids
        if (storageLimit === null || storageLimit === undefined) {
            // Log this situation to Sentry for monitoring
            Sentry.captureMessage('Spending Limit Not Set - Allowing Bid', {
                level: "info",
                tags: {
                    feature: 'spending_limits',
                    warningType: 'limit_not_configured',
                },
                contexts: {
                    bidding: {
                        requestedAmount: amount,
                        limitStatus: 'not_set',
                        limitValue: storageLimit,
                    },
                    user: {
                        userId: store.getters['userId'],
                    },
                },
                extra: {
                    note: 'Bid allowed because spending limit is not configured',
                }
            });

            return {
                isValid: true,
                errorMessage: '',
                moneyLimit: null,
                carsLimit: null
            };
        }

        // Get data from cookie
        let soldCars = [];
        const cookieValue = document.cookie
            .split('; ')
            .find(row => row.startsWith(cookieName + '='));

        if (cookieValue) {
            soldCars = JSON.parse(cookieValue.split('=')[1]);
        }

        // Calculate money limit
        let moneyLimit = storageLimit;
        if (soldCars.length > 0) {
            const spentMoney = soldCars.reduce((sum, car) => sum + car.price, 0);
            moneyLimit = storageLimit - spentMoney;
        }

        // Calculate cars limit
        let maxCarsLimit;

        if (soldCars.length === 0 && storageLimit > 0) {
            maxCarsLimit = 1;
        } else {
            maxCarsLimit = Math.floor(storageLimit / 6600);
        }

        // Limit maximum number of cars to 20
        maxCarsLimit = Math.min(maxCarsLimit, 20);
        const carsLimit = soldCars.length > 0 ? maxCarsLimit - soldCars.length : maxCarsLimit;

        // Check limits and form error message
        let errorMessage = '';

        if (moneyLimit < amount && carsLimit <= 0) {
            errorMessage = 'Both money limit and cars limit have been reached';
        } else if (moneyLimit < amount) {
            errorMessage = 'Money spending limit has been reached';
        } else if (carsLimit <= 0) {
            errorMessage = 'Cars limit has been reached';
        }

        return {
            isValid: !errorMessage,
            errorMessage,
            moneyLimit,
            carsLimit
        };
    }

    /**
     * Sends limit exceeded event to Sentry with structured context
     * @param {string} methodName - Name of the method where limit was exceeded
     * @param {object} validation - Validation result object
     * @param {object} params - Additional parameters
     * @private
     */
    _sendLimitExceededEvent(methodName, validation, params) {
        Sentry.captureMessage(`Spending Limit Exceeded in ${methodName}`, {
            level: "warning",
            tags: {
                feature: 'spending_limits',
                errorType: 'limit_exceeded',
                method: methodName,
            },
            contexts: {
                limits: {
                    moneyLimit: validation.moneyLimit,
                    carsLimit: validation.carsLimit,
                    limitType: validation.errorMessage,
                },
                bidding: {
                    requestedAmount: params.amount,
                    auctionId: params.auctionId,
                    itemId: params.itemId,
                },
                user: {
                    userId: params.userId,
                },
            },
            extra: {
                userVisibleError: validation.errorMessage,
                method: methodName,
                timestamp: new Date().toISOString(),
            }
        });
    }

    /**
     * Places a bid for an auction item
     * @param {string} InternalAuctionId - Auction identifier
     * @param {string} InternalAuctionItemId - Item identifier in the auction
     * @param {number} amount - Bid amount
     * @param {string} userId - User identifier
     * @returns {Promise} - Returns a promise that resolves when the bid is placed or rejects with an error
     */
    placeBid(InternalAuctionId, InternalAuctionItemId, amount, userId) {
        if (!this.iaaiClient || typeof this.iaaiClient.placeBid !== 'function') {
            // Capture client state to Sentry and return a rejected promise
            Sentry.captureMessage('placeBid called without initialized iaaiClient', {
                level: 'error',
                tags: {
                    feature: 'bidding',
                    errorType: 'client_not_initialized',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        bidAmount: amount,
                    },
                    client: {
                        hasClient: !!this.iaaiClient,
                        hasMethod: !!(this.iaaiClient && this.iaaiClient.placeBid),
                    },
                }
            });

            const error = new Error('Connection error. Please refresh the page and try again.');
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: error.message
            });
            return Promise.reject(error);
        }
        const validation = this._checkSpendingLimits(amount);

        // Get StockNumber from store for tracking
        const auctionItem = store.state.auctions.auction_item?.[InternalAuctionId]?.[InternalAuctionItemId];
        const stockNumber = auctionItem?.StockNumber || null;

        if (!validation.isValid) {
            // Send tracking event when bid limit is reached
            sendTrackingEvent('bid_limit_reached', {
                auction_id: InternalAuctionId,
                bid_amount: amount,
                limit_type: validation.errorMessage,
                bidder_id: userId,
                stock_number: stockNumber
            });

            // Send event to Sentry
            this._sendLimitExceededEvent('placeBid', validation, {
                amount,
                userId,
                auctionId: InternalAuctionId,
                itemId: InternalAuctionItemId
            });

            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: validation.errorMessage
            });
            return Promise.reject(new Error(validation.errorMessage));
        }

        // Add breadcrumb for successful bid attempt
        if (window.Sentry) {
            window.Sentry.addBreadcrumb({
                category: 'bidding',
                message: 'Manual bid placed',
                level: 'info',
                data: {
                    auctionId: InternalAuctionId,
                    itemId: InternalAuctionItemId,
                    amount: amount,
                }
            });
        }

        // Send tracking event for placing bid
        sendTrackingEvent('place_bid', {
            auction_id: InternalAuctionId,
            bid_amount: amount,
            bidder_id: userId,
            stock_number: stockNumber
        });

        try {
            const result = this.iaaiClient.placeBid(InternalAuctionId, InternalAuctionItemId, amount, userId);

            // Ensure we have a Promise before calling .catch
            if (!result || typeof result.catch !== 'function') {
                const error = new Error('Connection error. Please refresh the page and try again.');
                Sentry.captureMessage('placeBid returned non-Promise value', {
                    level: 'warning',
                    tags: {
                        feature: 'bidding',
                        errorType: 'invalid_return_type',
                    },
                    contexts: {
                        bidding: {
                            auctionId: InternalAuctionId,
                            itemId: InternalAuctionItemId,
                            bidAmount: amount,
                        },
                        client: {
                            hasClient: !!this.iaaiClient,
                            hasMethod: !!(this.iaaiClient && this.iaaiClient.placeBid),
                            returnType: typeof result,
                            returnValue: result,
                        },
                    }
                });
                store.commit('SET_BIDDING_ERROR', {
                    InternalAuctionId,
                    InternalAuctionItemId,
                    BiddingError: error.message
                });
                return Promise.reject(error);
            }

            return result.catch(error => {
                // Capture error with full context before rejecting
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                            feature: 'bidding',
                            errorType: 'manual_bid_failed',
                        },
                        contexts: {
                            bidding: {
                                auctionId: InternalAuctionId,
                                itemId: InternalAuctionItemId,
                                bidAmount: amount,
                            },
                            user: {
                                userId: userId,
                            },
                        },
                        extra: {
                            errorMessage: error.message,
                            stockNumber: stockNumber,
                        }
                    });
                }
                // Re-throw to maintain promise chain
                throw error;
            });
        } catch (error) {
            // Catch synchronous errors from placeBid call
            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    feature: 'bidding',
                    errorType: 'place_bid_sync_error',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        bidAmount: amount,
                    },
                }
            });
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: 'Connection error. Please refresh the page and try again.'
            });
            return Promise.reject(error);
        }
    }

    /**
     * Places an auto bid for an auction item
     * @param {string} InternalAuctionId - Auction identifier
     * @param {string} InternalAuctionItemId - Item identifier in the auction
     * @param {number} amount - Auto bid amount
     * @param {string} userId - User identifier
     * @returns {Promise} - Returns a promise that resolves when the auto bid is placed or rejects with an error
     */
    placeAutoBid(InternalAuctionId, InternalAuctionItemId, amount, userId) {
        if (!this.iaaiClient || typeof this.iaaiClient.placeAutoBid !== 'function') {
            Sentry.captureMessage('placeAutoBid called without initialized iaaiClient', {
                level: 'error',
                tags: {
                    feature: 'auto_bidding',
                    errorType: 'client_not_initialized',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        autoBidAmount: amount,
                    },
                    client: {
                        hasClient: !!this.iaaiClient,
                        hasMethod: !!(this.iaaiClient && this.iaaiClient.placeAutoBid),
                    },
                }
            });

            const error = new Error('Connection error. Please refresh the page and try again.');
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: error.message
            });
            return Promise.reject(error);
        }
        const validation = this._checkSpendingLimits(amount);

        if (!validation.isValid) {
            // Send event to Sentry
            this._sendLimitExceededEvent('placeAutoBid', validation, {
                amount,
                userId,
                auctionId: InternalAuctionId,
                itemId: InternalAuctionItemId
            });

            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: validation.errorMessage
            });
            return Promise.reject(new Error(validation.errorMessage));
        }

        try {
            const result = this.iaaiClient.placeAutoBid(InternalAuctionId, InternalAuctionItemId, amount, userId);

            // Ensure we have a Promise before calling .catch
            if (!result || typeof result.catch !== 'function') {
                const error = new Error('Connection error. Please refresh the page and try again.');
                Sentry.captureMessage('placeAutoBid returned non-Promise value', {
                    level: 'warning',
                    tags: {
                        feature: 'auto_bidding',
                        errorType: 'invalid_return_type',
                    },
                    contexts: {
                        bidding: {
                            auctionId: InternalAuctionId,
                            itemId: InternalAuctionItemId,
                            autoBidAmount: amount,
                        },
                        client: {
                            hasClient: !!this.iaaiClient,
                            hasMethod: !!(this.iaaiClient && this.iaaiClient.placeAutoBid),
                            returnType: typeof result,
                            returnValue: result,
                        },
                    }
                });
                store.commit('SET_BIDDING_ERROR', {
                    InternalAuctionId,
                    InternalAuctionItemId,
                    BiddingError: error.message
                });
                return Promise.reject(error);
            }

            return result.catch(error => {
                // Capture error with full context before rejecting
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                            feature: 'auto_bidding',
                            errorType: 'place_autobid_failed',
                        },
                        contexts: {
                            bidding: {
                                auctionId: InternalAuctionId,
                                itemId: InternalAuctionItemId,
                                autoBidAmount: amount,
                            },
                            user: {
                                userId: userId,
                            },
                        },
                        extra: {
                            errorMessage: error.message,
                        }
                    });
                }
                // Re-throw to maintain promise chain
                throw error;
            });
        } catch (error) {
            // Catch synchronous errors from placeAutoBid call
            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    feature: 'auto_bidding',
                    errorType: 'place_autobid_sync_error',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        autoBidAmount: amount,
                    },
                }
            });
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: 'Connection error. Please refresh the page and try again.'
            });
            return Promise.reject(error);
        }
    }

    /**
     * Modifies an existing auto bid for an auction item
     * @param {string} InternalAuctionId - Auction identifier
     * @param {string} InternalAuctionItemId - Item identifier in the auction
     * @param {number} amount - New auto bid amount
     * @param {string} userId - User identifier
     * @returns {Promise} - Returns a promise that resolves when the auto bid is modified or rejects with an error
     */
    modifyAutoBid(InternalAuctionId, InternalAuctionItemId, amount, userId) {
        if (!this.iaaiClient || typeof this.iaaiClient.modifyAutoBid !== 'function') {
            Sentry.captureMessage('modifyAutoBid called without initialized iaaiClient', {
                level: 'error',
                tags: {
                    feature: 'auto_bidding',
                    errorType: 'client_not_initialized',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        autoBidAmount: amount,
                    },
                    client: {
                        hasClient: !!this.iaaiClient,
                        hasMethod: !!(this.iaaiClient && this.iaaiClient.modifyAutoBid),
                    },
                }
            });

            const error = new Error('Connection error. Please refresh the page and try again.');
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: error.message
            });
            return Promise.reject(error);
        }
        const validation = this._checkSpendingLimits(amount);

        if (!validation.isValid) {
            // Send event to Sentry
            this._sendLimitExceededEvent('modifyAutoBid', validation, {
                amount,
                userId,
                auctionId: InternalAuctionId,
                itemId: InternalAuctionItemId
            });

            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: validation.errorMessage
            });
            return Promise.reject(new Error(validation.errorMessage));
        }

        try {
            const result = this.iaaiClient.modifyAutoBid(InternalAuctionId, InternalAuctionItemId, amount, userId);

            // Ensure we have a Promise before calling .catch
            if (!result || typeof result.catch !== 'function') {
                const error = new Error('Connection error. Please refresh the page and try again.');
                Sentry.captureMessage('modifyAutoBid returned non-Promise value', {
                    level: 'warning',
                    tags: {
                        feature: 'auto_bidding',
                        errorType: 'invalid_return_type',
                    },
                    contexts: {
                        bidding: {
                            auctionId: InternalAuctionId,
                            itemId: InternalAuctionItemId,
                            autoBidAmount: amount,
                        },
                        client: {
                            hasClient: !!this.iaaiClient,
                            hasMethod: !!(this.iaaiClient && this.iaaiClient.modifyAutoBid),
                            returnType: typeof result,
                            returnValue: result,
                        },
                    }
                });
                store.commit('SET_BIDDING_ERROR', {
                    InternalAuctionId,
                    InternalAuctionItemId,
                    BiddingError: error.message
                });
                return Promise.reject(error);
            }

            return result.catch(error => {
                // Capture error with full context before rejecting
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                            feature: 'auto_bidding',
                            errorType: 'modify_autobid_failed',
                        },
                        contexts: {
                            bidding: {
                                auctionId: InternalAuctionId,
                                itemId: InternalAuctionItemId,
                                autoBidAmount: amount,
                            },
                            user: {
                                userId: userId,
                            },
                        },
                        extra: {
                            errorMessage: error.message,
                        }
                    });
                }
                // Re-throw to maintain promise chain
                throw error;
            });
        } catch (error) {
            // Catch synchronous errors from modifyAutoBid call
            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    feature: 'auto_bidding',
                    errorType: 'modify_autobid_sync_error',
                },
                contexts: {
                    bidding: {
                        auctionId: InternalAuctionId,
                        itemId: InternalAuctionItemId,
                        autoBidAmount: amount,
                    },
                }
            });
            store.commit('SET_BIDDING_ERROR', {
                InternalAuctionId,
                InternalAuctionItemId,
                BiddingError: 'Connection error. Please refresh the page and try again.'
            });
            return Promise.reject(error);
        }
    }
}

export default IaaiService
